import React, { useEffect, useState, useRef } from 'react';
import { COLORS } from '../data/mockData';
import {
  adminBulkPhiZip, adminBulkPhiCsv, fetchPhiAuditLog, listPhiRecords,
} from '../services/api';
import {
  ShieldCheck, FileArchive, FileSpreadsheet, ListChecks, Loader2, AlertTriangle, CheckCircle2, Upload,
} from 'lucide-react';
import { humanizeApiError } from '../lib/errors';
import ResumableUploadModal from '../components/ResumableUploadModal';
import MemberPicker from '../components/MemberPicker';

export default function AdminMedicalPage() {
  const [tab, setTab] = useState('zip');
  const [zipResults, setZipResults] = useState(null);
  const [csvResults, setCsvResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const zipRef = useRef(null);
  const csvRef = useRef(null);
  const [csvType, setCsvType] = useState('lab');
  const [resumableOpen, setResumableOpen] = useState(false);
  const [resumableTarget, setResumableTarget] = useState(null);  // {user_id, email, name}
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, l] = await Promise.all([
          fetchPhiAuditLog({ limit: 100 }),
          listPhiRecords({ limit: 1 }),
        ]);
        setAuditLog(a.items || []);
        setStats({ total: l.total || 0 });
      } catch {}
    })();
  }, []);

  const onZipUpload = async (e) => {
    e.preventDefault();
    setError(''); setZipResults(null);
    const f = zipRef.current?.files?.[0];
    if (!f) return setError('Choose a .zip first');
    setLoading(true);
    try {
      const res = await adminBulkPhiZip(f);
      setZipResults(res.results || []);
      const a = await fetchPhiAuditLog({ limit: 100 });
      setAuditLog(a.items || []);
    } catch (err) {
      setError(humanizeApiError(err, 'Upload failed'));
    } finally { setLoading(false); }
  };

  const onCsvUpload = async (e) => {
    e.preventDefault();
    setError(''); setCsvResults(null);
    const f = csvRef.current?.files?.[0];
    if (!f) return setError('Choose a .csv first');
    setLoading(true);
    try {
      const res = await adminBulkPhiCsv(f, csvType);
      setCsvResults(res.results || []);
      const a = await fetchPhiAuditLog({ limit: 100 });
      setAuditLog(a.items || []);
    } catch (err) {
      setError(humanizeApiError(err, 'Upload failed'));
    } finally { setLoading(false); }
  };

  const Tab = ({ id, label, icon: Icon }) => (
    <button data-testid={`admin-phi-tab-${id}`} onClick={() => setTab(id)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
               backgroundColor: tab === id ? COLORS.primary : 'transparent',
               color: tab === id ? '#FFF' : COLORS.textPrimary,
               border: `1px solid ${tab === id ? COLORS.primary : COLORS.surfaceSecondary}`,
               borderRadius: 9999, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div data-testid="admin-medical-page" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <ShieldCheck size={22} color={COLORS.primary} />
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, fontFamily: 'Outfit, sans-serif', color: COLORS.textPrimary }}>
          PHI Vault — Bulk Upload & Audit
        </h2>
      </div>
      <p style={{ color: COLORS.textSecondary, fontSize: 13, marginTop: 0 }}>
        {stats.total} record{stats.total === 1 ? '' : 's'} encrypted in the vault.
        Single-file uploads on behalf of a member: ask the member to upload from their own
        account, or use the Members tab to impersonate (audit trail records both actors).
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' }}>
        <Tab id="zip" label="ZIP + manifest" icon={FileArchive} />
        <Tab id="csv" label="CSV (structured)" icon={FileSpreadsheet} />
        <Tab id="audit" label="Audit log" icon={ListChecks} />
        <button data-testid="admin-phi-resumable-open" onClick={() => setPickerOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                   backgroundColor: 'transparent', color: COLORS.primary,
                   border: `1px solid ${COLORS.primary}`,
                   borderRadius: 9999, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Upload size={14} /> Single resumable
        </button>
      </div>

      {error && (
        <div style={{ backgroundColor: '#FEE2E2', color: '#B22222', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {tab === 'zip' && (
        <form onSubmit={onZipUpload} data-testid="admin-phi-zip-form" style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 18, border: `1px solid ${COLORS.surfaceSecondary}` }}>
          <h4 style={{ marginTop: 0, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Bulk ZIP upload</h4>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5 }}>
            ZIP must contain a <code style={{ background: '#EEE', padding: '1px 5px', borderRadius: 3 }}>manifest.csv</code> at the root with columns:
            <br />
            <code>member_email, file_name, record_type, record_date, notes</code>
          </p>
          <input ref={zipRef} type="file" accept=".zip,application/zip" data-testid="admin-phi-zip-input"
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8, marginRight: 12 }} />
          <button type="submit" disabled={loading} data-testid="admin-phi-zip-submit"
            style={{ backgroundColor: COLORS.primary, color: '#FFF', border: 'none', borderRadius: 9999, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? <Loader2 size={14} className="spin" /> : 'Upload'}
          </button>
          {zipResults && (
            <div style={{ marginTop: 14, maxHeight: 240, overflowY: 'auto' }}>
              {zipResults.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, borderBottom: '1px solid #EEE' }}>
                  {r.ok ? <CheckCircle2 size={14} color="#3CB371" /> : <AlertTriangle size={14} color="#B22222" />}
                  <span style={{ flex: 1, color: COLORS.textPrimary }}>{r.file_name}</span>
                  <span style={{ color: r.ok ? '#3CB371' : '#B22222', fontSize: 12 }}>{r.ok ? r.record_id : r.error}</span>
                </div>
              ))}
            </div>
          )}
        </form>
      )}

      {tab === 'csv' && (
        <form onSubmit={onCsvUpload} data-testid="admin-phi-csv-form" style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 18, border: `1px solid ${COLORS.surfaceSecondary}` }}>
          <h4 style={{ marginTop: 0, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Structured CSV (no files)</h4>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5 }}>
            One row per record. Required: <code>member_email, record_date</code>. Any other columns are stored as the structured payload (encrypted).
          </p>
          <input ref={csvRef} type="file" accept=".csv,text/csv" data-testid="admin-phi-csv-input"
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8, marginRight: 12 }} />
          <select value={csvType} onChange={e => setCsvType(e.target.value)} data-testid="admin-phi-csv-type"
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8, marginRight: 12 }}>
            <option value="lab">Lab values</option>
            <option value="note">Note</option>
            <option value="other">Other</option>
          </select>
          <button type="submit" disabled={loading} data-testid="admin-phi-csv-submit"
            style={{ backgroundColor: COLORS.primary, color: '#FFF', border: 'none', borderRadius: 9999, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? <Loader2 size={14} className="spin" /> : 'Upload'}
          </button>
          {csvResults && (
            <div style={{ marginTop: 14, maxHeight: 240, overflowY: 'auto' }}>
              {csvResults.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, borderBottom: '1px solid #EEE' }}>
                  {r.ok ? <CheckCircle2 size={14} color="#3CB371" /> : <AlertTriangle size={14} color="#B22222" />}
                  <span style={{ flex: 1, color: COLORS.textPrimary }}>Row {r.row}</span>
                  <span style={{ color: r.ok ? '#3CB371' : '#B22222', fontSize: 12 }}>{r.ok ? r.record_id : r.error}</span>
                </div>
              ))}
            </div>
          )}
        </form>
      )}

      {tab === 'audit' && (
        <div data-testid="admin-phi-audit" style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 18, border: `1px solid ${COLORS.surfaceSecondary}` }}>
          <h4 style={{ marginTop: 0, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Audit Log (last 100)</h4>
          <div style={{ maxHeight: 460, overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: COLORS.textSecondary, borderBottom: '1px solid #EEE' }}>
                  <th style={{ padding: '8px 6px' }}>Time</th>
                  <th style={{ padding: '8px 6px' }}>Action</th>
                  <th style={{ padding: '8px 6px' }}>Actor</th>
                  <th style={{ padding: '8px 6px' }}>Record</th>
                  <th style={{ padding: '8px 6px' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #F5F5F5' }}>
                    <td style={{ padding: '6px 6px', whiteSpace: 'nowrap' }}>{new Date(e.timestamp).toLocaleString()}</td>
                    <td style={{ padding: '6px 6px', fontWeight: 600 }}>{e.action}</td>
                    <td style={{ padding: '6px 6px' }}>{e.actor_email}</td>
                    <td style={{ padding: '6px 6px', fontFamily: 'monospace' }}>{e.record_id || '—'}</td>
                    <td style={{ padding: '6px 6px', fontFamily: 'monospace' }}>{e.ip || '—'}</td>
                  </tr>
                ))}
                {auditLog.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: COLORS.textSecondary }}>No audit events yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MemberPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(u) => { setResumableTarget(u); setPickerOpen(false); setResumableOpen(true); }}
      />

      <ResumableUploadModal
        open={resumableOpen}
        onClose={() => { setResumableOpen(false); setResumableTarget(null); }}
        onUploaded={() => { setResumableOpen(false); setResumableTarget(null); fetchPhiAuditLog({ limit: 100 }).then(a => setAuditLog(a.items || [])); }}
        targetUserId={resumableTarget?.user_id || ''}
        targetLabel={resumableTarget ? `${resumableTarget.name || resumableTarget.email} (${resumableTarget.email})` : ''}
      />
    </div>
  );
}
